import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Building2, MapPin, FileText, Users, Calendar, Save, AlertCircle, Plus, Trash2, Search } from 'lucide-react';
import { createGpo, updateGpo, getGpoContracts, getGpo, getCustomers, addGpoMember, removeGpoMember, getGpos } from '../config/api';
import { toast } from 'react-hot-toast';
import { StateSelect, CountrySelect, CityInput } from './geo/GeoSelects';

// Roster of member customers for a GPO — add/list/remove.
const GpoMembersPanel = ({ gpoId, isViewMode }) => {
  const [members, setMembers] = useState([]);
  const [contractCustomers, setContractCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getGpo(gpoId);
      const gpo = res?.data || res || {};
      setMembers(gpo.members || []);
      // Derive the unique customers party to this GPO's contracts, tracking which
      // contracts each appears on. This is a read-only view, separate from the
      // enrolled membership roster above.
      const byId = new Map();
      (gpo.contracts || []).forEach((c) => {
        (c.contractCustomers || []).forEach((cc) => {
          const cust = cc.customer;
          if (!cust) return;
          if (!byId.has(cust.id)) byId.set(cust.id, { ...cust, contractNumbers: [] });
          if (c.number) byId.get(cust.id).contractNumbers.push(c.number);
        });
      });
      setContractCustomers([...byId.values()]);
    } catch (e) { toast.error('Failed to load members'); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (gpoId) load(); /* eslint-disable-next-line */ }, [gpoId]);

  useEffect(() => {
    let alive = true;
    getCustomers({ search, limit: 25 }).then((d) => { if (alive) setCustomers(d?.customers || []); }).catch(() => {});
    return () => { alive = false; };
  }, [search]);

  const memberIds = new Set(members.map((m) => m.customerId));
  const add = async () => {
    if (!selected) return;
    setBusy(true);
    try { await addGpoMember(gpoId, { customerId: selected }); toast.success('Member added'); setSelected(''); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed to add member'); }
    finally { setBusy(false); }
  };
  const remove = async (customerId, name) => {
    if (!window.confirm(`Remove ${name || 'this customer'} from the GPO?`)) return;
    try { await removeGpoMember(gpoId, customerId); toast.success('Member removed'); load(); }
    catch (e) { toast.error('Failed to remove member'); }
  };
  const promote = async (customerId, name) => {
    try { await addGpoMember(gpoId, { customerId }); toast.success(`${name || 'Customer'} added to roster`); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed to add to roster'); }
  };
  // Contract customers not already on the enrolled roster.
  const contractOnly = contractCustomers.filter((c) => !memberIds.has(c.id));

  return (
    <div className="space-y-4">
      {!isViewMode && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="mb-2 text-sm font-medium text-gray-700">Add a member customer</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customers…" className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-3 text-sm" />
            </div>
            <select value={selected} onChange={(e) => setSelected(e.target.value)} className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm">
              <option value="">Select a customer…</option>
              {customers.filter((c) => !memberIds.has(c.id)).map((c) => (
                <option key={c.id} value={c.id}>{c.name}{c.city ? ` — ${c.city}, ${c.state}` : ''}</option>
              ))}
            </select>
            <button onClick={add} disabled={!selected || busy} className="inline-flex items-center justify-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="py-6 text-center text-sm text-gray-400">Loading members…</p>
      ) : members.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 py-8 text-center">
          <Users className="mx-auto mb-2 h-8 w-8 text-gray-300" />
          <p className="text-sm text-gray-500">No member customers yet.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left">Customer</th>
                <th className="px-4 py-2 text-left">Class of Trade</th>
                <th className="px-4 py-2 text-left">Location</th>
                {!isViewMode && <th className="px-4 py-2"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {members.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-900">{m.customer?.name || m.customerId}</td>
                  <td className="px-4 py-2.5 text-gray-600">{m.customer?.classOfTrade || m.customer?.type || '—'}</td>
                  <td className="px-4 py-2.5 text-gray-600">{[m.customer?.city, m.customer?.state].filter(Boolean).join(', ') || '—'}</td>
                  {!isViewMode && (
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => remove(m.customerId, m.customer?.name)} className="text-red-600 hover:text-red-800" title="Remove member"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Read-only: customers party to this GPO's contracts but NOT on the roster */}
      {!loading && contractOnly.length > 0 && (
        <div className="rounded-lg border border-gray-200">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2">
            <p className="text-sm font-medium text-gray-700">Customers via this GPO's contracts</p>
            <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600">{contractOnly.length}</span>
          </div>
          <p className="px-4 pt-2 text-xs text-gray-500">
            These customers are on this GPO's contracts but are not enrolled members. They're already eligible for pricing via the contract{!isViewMode && ' — add them to the roster if you want them tracked as formal members'}.
          </p>
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="text-xs uppercase tracking-wide text-gray-400">
              <tr>
                <th className="px-4 py-2 text-left">Customer</th>
                <th className="px-4 py-2 text-left">Class of Trade</th>
                <th className="px-4 py-2 text-left">On Contracts</th>
                {!isViewMode && <th className="px-4 py-2"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {contractOnly.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-900">{c.name}</td>
                  <td className="px-4 py-2.5 text-gray-600">{c.classOfTrade || c.type || '—'}</td>
                  <td className="px-4 py-2.5 text-gray-500">{[...new Set(c.contractNumbers)].join(', ') || '—'}</td>
                  {!isViewMode && (
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => promote(c.id, c.name)} className="inline-flex items-center gap-1 rounded-md border border-blue-200 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50" title="Add to membership roster">
                        <Plus className="h-3.5 w-3.5" /> Add to roster
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const GPOModal = ({ gpo, isOpen, onClose, isViewMode = false }) => {
  const queryClient = useQueryClient();
  
  // Form state
  const emptyForm = {
    name: '', code: '', deaNumber: '', hinNumber: '', membershipId: '', parentGpoId: '',
    address1: '', address2: '', city: '', state: '', postal: '', country: ''
  };
  const [formData, setFormData] = useState(emptyForm);
  const [gpoOptions, setGpoOptions] = useState([]);
  useEffect(() => {
    let alive = true;
    getGpos({ limit: 100 }).then((res) => { if (alive) setGpoOptions(res?.data?.gpos || res?.gpos || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  
  // Contracts data for view mode
  const [contractsData, setContractsData] = useState(null);
  const [contractsLoading, setContractsLoading] = useState(false);

  // Initialize form data
  useEffect(() => {
    if (gpo) {
      setFormData({
        name: gpo.name || '',
        code: gpo.code || '',
        deaNumber: gpo.deaNumber || '',
        hinNumber: gpo.hinNumber || '',
        membershipId: gpo.membershipId || '',
        parentGpoId: gpo.parentGpoId || '',
        address1: gpo.address1 || '',
        address2: gpo.address2 || '',
        city: gpo.city || '',
        state: gpo.state || '',
        postal: gpo.postal || '',
        country: gpo.country || ''
      });
    } else {
      setFormData(emptyForm);
    }
    setErrors({});
  }, [gpo]);

  // Load contracts when in view mode
  useEffect(() => {
    if (gpo && activeTab === 'contracts') {
      loadContracts();
    }
  }, [isViewMode, gpo, activeTab]);

  const loadContracts = async () => {
    if (!gpo) return;
    
    setContractsLoading(true);
    try {
      const response = await getGpoContracts(gpo.id);
      setContractsData(response.data);
    } catch (error) {
      console.error('Error loading contracts:', error);
      toast.error('Failed to load contracts');
    } finally {
      setContractsLoading(false);
    }
  };

  // Create GPO mutation
  const createMutation = useMutation({
    mutationFn: createGpo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gpos'] });
      queryClient.invalidateQueries({ queryKey: ['gpoStatistics'] });
      toast.success('GPO created successfully');
      onClose();
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || 'Failed to create GPO';
      toast.error(errorMessage);
    }
  });

  // Update GPO mutation
  const updateMutation = useMutation({
    mutationFn: (data) => updateGpo(gpo.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gpos'] });
      queryClient.invalidateQueries({ queryKey: ['gpoStatistics'] });
      toast.success('GPO updated successfully');
      onClose();
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || 'Failed to update GPO';
      toast.error(errorMessage);
    }
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'GPO name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'GPO name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'GPO name must be less than 100 characters';
    }
    
    if (formData.address1 && formData.address1.length > 100) {
      newErrors.address1 = 'Address1 must be less than 100 characters';
    }
    
    if (formData.address2 && formData.address2.length > 100) {
      newErrors.address2 = 'Address2 must be less than 100 characters';
    }
    
    if (formData.city && formData.city.length > 50) {
      newErrors.city = 'City must be less than 50 characters';
    }
    
    if (formData.state && formData.state.length > 50) {
      newErrors.state = 'State must be less than 50 characters';
    }
    
    if (formData.postal && formData.postal.length > 20) {
      newErrors.postal = 'Postal code must be less than 20 characters';
    }
    
    if (formData.country && formData.country.length > 50) {
      newErrors.country = 'Country must be less than 50 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const submitData = {
        name: formData.name.trim(),
        code: formData.code.trim() || null,
        deaNumber: formData.deaNumber.trim() || null,
        hinNumber: formData.hinNumber.trim() || null,
        membershipId: formData.membershipId.trim() || null,
        parentGpoId: formData.parentGpoId || null,
        address1: formData.address1.trim() || null,
        address2: formData.address2.trim() || null,
        city: formData.city.trim() || null,
        state: formData.state.trim() || null,
        postal: formData.postal.trim() || null,
        country: formData.country.trim() || null
      };
      
      if (gpo) {
        await updateMutation.mutateAsync(submitData);
      } else {
        await createMutation.mutateAsync(submitData);
      }
    } catch (error) {
      // Error handling is done in mutation onError
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getContractStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'text-green-600 bg-green-100';
      case 'DRAFT':
        return 'text-yellow-600 bg-yellow-100';
      case 'EXPIRED':
        return 'text-red-600 bg-red-100';
      case 'ARCHIVED':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Building2 className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {isViewMode ? 'View GPO' : gpo ? 'Edit GPO' : 'Create New GPO'}
              </h2>
              <p className="text-sm text-gray-600">
                {isViewMode ? 'View GPO details and associated contracts' : 'Manage GPO information and settings'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            title="Close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        {gpo && (
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('details')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'details'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                title="Details"
              >
                Details
              </button>
              <button
                onClick={() => setActiveTab('contracts')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'contracts'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                title="Contracts"
              >
                Contracts ({gpo?._count?.contracts || 0})
              </button>
              <button
                onClick={() => setActiveTab('members')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'members'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                title="Members"
              >
                Members ({gpo?._count?.members || 0})
              </button>
            </nav>
          </div>
        )}

        {/* Content — flex-1 + min-h-0 lets it take the remaining height and scroll,
            so the pinned footer below is always visible even with long lists. */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {activeTab === 'details' ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      GPO Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.name ? 'border-red-300' : 'border-gray-300'
                      } ${isViewMode ? 'bg-gray-50' : ''}`}
                      placeholder="Enter GPO name"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.name}
                      </p>
                    )}
                  </div>
                  {[
                    { name: 'code', label: 'GPO Code', placeholder: 'e.g. GPO-1234' },
                    { name: 'membershipId', label: 'Membership ID', placeholder: "GPO's registration id" },
                    { name: 'deaNumber', label: 'DEA Number', placeholder: 'GPO DEA' },
                    { name: 'hinNumber', label: 'HIN Number', placeholder: 'GPO HIN' }
                  ].map((f) => (
                    <div key={f.name}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                      <input
                        type="text"
                        name={f.name}
                        value={formData[f.name]}
                        onChange={handleInputChange}
                        disabled={isViewMode}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isViewMode ? 'bg-gray-50' : ''}`}
                        placeholder={f.placeholder}
                      />
                    </div>
                  ))}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent GPO (affiliation)</label>
                    <select
                      name="parentGpoId"
                      value={formData.parentGpoId}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isViewMode ? 'bg-gray-50' : ''}`}
                    >
                      <option value="">None (top-level GPO)</option>
                      {gpoOptions.filter((g) => g.id !== gpo?.id).map((g) => (
                        <option key={g.id} value={g.id}>{g.name}{g.code ? ` (${g.code})` : ''}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">If this GPO is an affiliate, its members qualify for the parent GPO's contract pricing.</p>
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Address Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address Line 1
                    </label>
                    <input
                      type="text"
                      name="address1"
                      value={formData.address1}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.address1 ? 'border-red-300' : 'border-gray-300'
                      } ${isViewMode ? 'bg-gray-50' : ''}`}
                      placeholder="Street address"
                    />
                    {errors.address1 && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.address1}
                      </p>
                    )}
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      name="address2"
                      value={formData.address2}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.address2 ? 'border-red-300' : 'border-gray-300'
                      } ${isViewMode ? 'bg-gray-50' : ''}`}
                      placeholder="Apartment, suite, etc."
                    />
                    {errors.address2 && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.address2}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <CityInput
                      name="city"
                      value={formData.city}
                      state={formData.state}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      error={!!errors.city}
                      placeholder="City"
                    />
                    {errors.city && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.city}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State/Province
                    </label>
                    <StateSelect
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      error={!!errors.state}
                    />
                    {errors.state && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.state}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      name="postal"
                      value={formData.postal}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.postal ? 'border-red-300' : 'border-gray-300'
                      } ${isViewMode ? 'bg-gray-50' : ''}`}
                      placeholder="ZIP or Postal Code"
                    />
                    {errors.postal && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.postal}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Country
                    </label>
                    <CountrySelect
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      error={!!errors.country}
                    />
                    {errors.country && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.country}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* View Mode - Additional Info */}
              {isViewMode && gpo && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        GPO ID
                      </label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-600">
                        {gpo.id}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Created Date
                      </label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-600">
                        {formatDate(gpo.createdAt)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Updated
                      </label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-600">
                        {formatDate(gpo.updatedAt)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Total Contracts
                      </label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-600">
                        {gpo._count?.contracts || 0} contracts
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </form>
          ) : activeTab === 'members' ? (
            <GpoMembersPanel gpoId={gpo?.id} isViewMode={isViewMode} />
          ) : (
            /* Contracts Tab */
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Associated Contracts</h3>
              
              {contractsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading contracts...</span>
                </div>
              ) : contractsData?.contracts?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contract Number
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Effective Period
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customers
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {contractsData.contracts.map((contract) => (
                        <tr key={contract.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {contract.number}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div className="max-w-xs truncate" title={contract.description}>
                              {contract.description}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {contract.type}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getContractStatusColor(contract.status)}`}>
                              {contract.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(contract.effectiveFrom)} - {formatDate(contract.effectiveTo)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {contract._count?.contractCustomers || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Contracts</h3>
                  <p className="text-gray-500">This GPO doesn't have any associated contracts yet.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer — pinned; never scrolls out of view */}
        {!isViewMode && (
          <div className="flex-shrink-0 flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Save GPO"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {gpo ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {gpo ? 'Update GPO' : 'Create GPO'}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GPOModal;
